import React, { useState, useEffect } from 'react';
import { RefreshCw, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';
import { apiService } from '../../services/apiService';
import { formatDateShort, formatSignedPct } from '../../utils/format';
import {
  BENCHMARKS, BENCHMARK_DASH, BENCHMARK_SYMBOLS, WindowReturn, seriesOf, windowReturn,
} from '../../utils/benchmarks';
import BenchmarkLegendItem from './BenchmarkLegendItem';

interface NavChartProps {
  agentId: string;
  startingCapital?: number;
}

interface PeriodStats {
  period_return_pct: number;
  period_pnl: number;
  max_drawdown_pct: number;
  sharpe_ratio: number;
  sortino_ratio: number;
  period_trades: number;
  period_win_rate: number;
  data_points: number;
}

const PERIODS = ['1W', '1M', '3M', 'YTD', '1Y', 'ALL'] as const;
// The agent's line: green when its NAV is up over the period, red when down (benchmark colours avoid both)
export const NAV_UP_COLOR = '#4ade80';
export const NAV_DOWN_COLOR = '#f87171';
const PERIOD_API_MAP: Record<string, string> = {
  '1W': '7d', '1M': '30d', '3M': '90d',
  'YTD': 'YTD', '1Y': '1Y', 'ALL': 'ALL',
};

export const NavChart: React.FC<NavChartProps> = ({ agentId, startingCapital = 250000 }) => {
  const [period, setPeriod] = useState<string>('ALL');
  const [history, setHistory] = useState<any[]>([]);
  const [stats, setStats] = useState<PeriodStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState<any[]>([]);
  // Each benchmark's return over the agent's window, with whether its last close is pending (B-050).
  const [benchmarkReturns, setBenchmarkReturns] = useState<Record<string, WindowReturn>>({});
  // B-050: benchmarks the route could not serve (its `_failed`, or all five when the request failed)
  const [benchFailed, setBenchFailed] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const apiPeriod = PERIOD_API_MAP[period];
        // First fetch NAV to know the agent's start date
        const [navData, perfData] = await Promise.all([
          apiService.getAgentNavHistory(agentId, apiPeriod),
          apiService.getAgentPeriodPerformance(agentId, apiPeriod),
        ]);

        const navHistory = navData.history || [];
        setHistory(navHistory);
        setStats(perfData);

        // Fetch benchmarks: for ALL, align to agent's start date; for periods, backend calculates
        const agentStartDate = navHistory.length > 0 ? navHistory[0].date : undefined;
        const benchFromDate = period === 'ALL' ? agentStartDate : undefined;
        const benchData = await apiService.getBenchmarks(apiPeriod, BENCHMARK_SYMBOLS, benchFromDate)
          .catch(() => ({ _failed: BENCHMARK_SYMBOLS }));
        const failed = (benchData as any)?._failed;
        setBenchFailed(Array.isArray(failed) ? failed : []);

        // Build unified chart data — always include benchmarks
        if (navHistory.length >= 2) {
          const baseNav = navHistory[0].nav;
          const allDates = new Set<string>();
          const agentByDate: Record<string, number> = {};

          for (const point of navHistory) {
            allDates.add(point.date);
            agentByDate[point.date] = point.nav;
          }

          const benchByDate: Record<string, Record<string, number>> = {};
          const benchReturns: Record<string, WindowReturn> = {};
          // YTD (B-050 follow-up): each benchmark runs from its prior-year close, as on the Scoreboard (the route
          // starts every series there); the agent's line still starts at its first NAV of the year. Every other
          // period rebases the benchmarks at the agent's first NAV.
          const ytd = period === 'YTD';

          for (const { symbol } of BENCHMARKS) {
            const points = seriesOf(benchData, symbol);
            if (points.length === 0) continue;
            const agentStartDate = navHistory[0].date;
            const agentEndDate = navHistory[navHistory.length - 1].date;
            const from = ytd ? undefined : agentStartDate;
            // The base: the close on or before the agent's start (the series' first close for YTD)
            let baseIdx = 0;
            for (let i = 0; i < points.length; i++) {
              if (from !== undefined && points[i].date <= from) baseIdx = i;
            }
            const benchBase = points[baseIdx].price;

            for (const bp of points) {
              if ((from === undefined || bp.date >= from) && bp.date <= agentEndDate) {
                allDates.add(bp.date);
                if (!benchByDate[bp.date]) benchByDate[bp.date] = {};
                benchByDate[bp.date][symbol] = ((bp.price - benchBase) / benchBase) * 100;
              }
            }

            // The legend value: the same base, to the last close on or before the agent's last NAV
            const r = windowReturn(points, from, agentEndDate);
            if (r) benchReturns[symbol] = r;
          }

          setBenchmarkReturns(benchReturns);

          // Sort and build with forward-fill
          const sortedDates = Array.from(allDates).sort();
          const merged: any[] = [];
          let lastAgentNav = baseNav;
          const lastBench: Record<string, number> = {};

          const agentStart = navHistory[0].date;
          for (const date of sortedDates) {
            if (agentByDate[date] !== undefined) lastAgentNav = agentByDate[date];
            const agentReturn = ((lastAgentNav - baseNav) / baseNav) * 100;

            // The agent's line from its first NAV only (under YTD the benchmarks start at the prior-year close)
            const row: any = { date };
            if (date >= agentStart) row.agent_return = parseFloat(agentReturn.toFixed(4));

            // Carried across days without a close, never past the benchmark's last close in the window
            for (const { symbol } of BENCHMARKS) {
              if (benchByDate[date]?.[symbol] !== undefined) lastBench[symbol] = benchByDate[date][symbol];
              if (lastBench[symbol] !== undefined && date <= (benchReturns[symbol]?.end ?? '')) {
                row[`${symbol}_return`] = parseFloat(lastBench[symbol].toFixed(4));
              }
            }

            merged.push(row);
          }

          setChartData(merged);
        } else {
          setChartData([]);
          setBenchmarkReturns({});
        }
      } catch (err) {
        console.error('Failed to fetch NAV data:', err);
        setHistory([]);
        setStats(null);
        setChartData([]);
        setBenchmarkReturns({});
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [agentId, period]);

  const isPositive = history.length >= 2
    ? history[history.length - 1].nav >= history[0].nav
    : true;
  const lineColor = isPositive ? NAV_UP_COLOR : NAV_DOWN_COLOR;

  // A line for each benchmark with a window return (two closes or more); the legend names all five
  const shownBenchmarks = BENCHMARKS.filter((b) => benchmarkReturns[b.symbol] !== undefined);
  const anyPending = shownBenchmarks.some((b) => benchmarkReturns[b.symbol]?.pending);
  const chartEnd: string | undefined = chartData[chartData.length - 1]?.date;

  return (
    <div className="space-y-4">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-cyan-400" /> Performance vs Market
        </h3>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`text-10 px-2.5 py-1 rounded-full font-medium transition-all ${
                period === p
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'bg-slate-800/40 text-slate-500 border border-slate-700/30 hover:text-slate-300'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="h-56 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 animate-spin text-cyan-400" />
        </div>
      ) : history.length < 2 ? (
        <div className="h-56 flex flex-col items-center justify-center bg-slate-800/30 rounded-xl border border-slate-700/20">
          <BarChart3 className="w-8 h-8 text-slate-600 mb-2" />
          <p className="text-xs text-slate-500">Not enough data yet</p>
          <p className="text-10 text-slate-600 mt-1">Snapshots accumulate daily — charts appear after a few days</p>
        </div>
      ) : (
        <div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDateShort}
                  stroke="#64748b"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={30}
                />
                <YAxis
                  tickFormatter={(v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`}
                  stroke="#64748b"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  width={55}
                />
                <ReferenceLine y={0} stroke="#475569" strokeDasharray="2 2" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    fontSize: '11px',
                  }}
                  labelFormatter={(label: any) => formatDateShort(String(label))}
                  formatter={(value: any, name: any) => {
                    const bench = BENCHMARKS.find((b) => `${b.symbol}_return` === name);
                    return [formatSignedPct(value), name === 'agent_return' ? 'Agent' : bench ? bench.symbol : name];
                  }}
                />
                {/* Agent line — solid & prominent */}
                <Line
                  type="monotone"
                  dataKey="agent_return"
                  stroke={lineColor}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 4 }}
                  name="agent_return"
                />
                {/* Benchmarks — dashed: SPY, QQQ, JEPI, QYLD, YMAX (B-050) */}
                {shownBenchmarks.map((b) => (
                  <Line
                    key={b.symbol}
                    type="monotone"
                    dataKey={`${b.symbol}_return`}
                    stroke={b.color}
                    strokeWidth={1.5}
                    strokeDasharray={BENCHMARK_DASH}
                    dot={false}
                    name={`${b.symbol}_return`}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* Legend */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 mt-2">
            <span className="flex items-center gap-1.5 text-10">
              <span className="w-4 h-[3px] rounded-sm" style={{ backgroundColor: lineColor, display: 'inline-block' }} />
              <span className="text-slate-300 font-medium">Agent {formatSignedPct(chartData[chartData.length - 1]?.agent_return ?? 0)}</span>
            </span>
            {BENCHMARKS.map((b) => (
              <BenchmarkLegendItem key={b.symbol} benchmark={b} value={benchmarkReturns[b.symbol] ?? null}
                failed={benchFailed.includes(b.symbol)} chartEnd={chartEnd} wide />
            ))}
          </div>
          {anyPending && (
            <p className="text-9 text-slate-500 text-center mt-1" data-testid="benchmark-pending-note">
              * The latest close is still provisional and may be revised by the vendor.
            </p>
          )}
        </div>
      )}

      {/* Period Stats */}
      {stats && stats.data_points >= 2 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <StatMini
            label="Return"
            value={`${stats.period_return_pct >= 0 ? '+' : '-'}${Math.abs(stats.period_return_pct).toFixed(2)}%`}
            sub={`${stats.period_pnl >= 0 ? '+' : '-'}$${Math.abs(stats.period_pnl).toFixed(0)}`}
            positive={stats.period_return_pct >= 0}
          />
          <StatMini
            label="Max Drawdown"
            value={`-${stats.max_drawdown_pct.toFixed(2)}%`}
            positive={false}
            neutral={stats.max_drawdown_pct === 0}
          />
          <StatMini
            label="Sharpe"
            value={stats.sharpe_ratio.toFixed(2)}
            positive={stats.sharpe_ratio > 0}
            neutral={stats.sharpe_ratio === 0}
          />
          <StatMini
            label="Trades"
            value={`${stats.period_trades}`}
            sub={`${stats.period_win_rate.toFixed(0)}% win`}
          />
          <StatMini
            label="Data Points"
            value={`${stats.data_points} days`}
          />
        </div>
      )}
    </div>
  );
};

const StatMini: React.FC<{
  label: string;
  value: string;
  sub?: string;
  positive?: boolean;
  neutral?: boolean;
}> = ({ label, value, sub, positive, neutral }) => (
  <div className="p-2.5 bg-slate-800/40 rounded-lg border border-slate-700/20">
    <div className="text-10 text-slate-500 uppercase tracking-wider">{label}</div>
    <div className={`text-sm font-bold tabular-nums ${
      neutral ? 'text-slate-400' : positive ? 'text-green-400' : positive === false ? 'text-red-400' : 'text-white'
    }`}>
      {value}
    </div>
    {sub && <div className="text-10 text-slate-500 tabular-nums">{sub}</div>}
  </div>
);

export default NavChart;
